<script lang="ts">
	import { onDestroy, onMount, type Snippet } from 'svelte';
	import './layout.css';
	import favicon from '$lib/assets/favicon.svg';
	import * as Sidebar from '$lib/components/ui/sidebar/index.js';
	import { Toaster, toast } from 'svelte-sonner';
	import PlusIcon from '@lucide/svelte/icons/plus';
	import BlocksIcon from '@lucide/svelte/icons/blocks';
	import ChartColumnIcon from '@lucide/svelte/icons/chart-column';
	import ClockIcon from '@lucide/svelte/icons/clock';
	import ShieldIcon from '@lucide/svelte/icons/shield';
	import FlaskConicalIcon from '@lucide/svelte/icons/flask-conical';
	import SessionList from '$lib/components/SessionList.svelte';
	import SharedWithMeList from '$lib/components/SharedWithMeList.svelte';
	import TrashList from '$lib/components/TrashList.svelte';
	import UpdateBanner from '$lib/components/UpdateBanner.svelte';
	import InstallPrompt from '$lib/components/InstallPrompt.svelte';
	import SidebarMobileOpener from '$lib/components/SidebarMobileOpener.svelte';
	import SidebarUsage from '$lib/components/SidebarUsage.svelte';
	import { edgeSwipe } from '$lib/actions/swipe';
	import { afterNavigate, beforeNavigate, goto, onNavigate } from '$app/navigation';
	import { page, updated } from '$app/state';
	import { refreshSessions } from '$lib/sessions-store.svelte';
	import { pollScheduledBadge, scheduledHasUnread } from '$lib/scheduled-store.svelte';
	import { refreshSharedSessions } from '$lib/shared-sessions-store.svelte';
	import { registerSW } from '$lib/sw-update.svelte';
	import LogOutIcon from '@lucide/svelte/icons/log-out';
	import RotateCcwIcon from '@lucide/svelte/icons/rotate-ccw';
	import SunIcon from '@lucide/svelte/icons/sun';
	import MoonIcon from '@lucide/svelte/icons/moon';
	import { resetOnboarding } from '$lib/marketplace-api';
	import { isDark, toggleTheme } from '$lib/theme.svelte';
	import {
		initAuth,
		login,
		logout,
		isAuthenticated,
		isDevBypass,
		authReady,
		profile,
		me,
		meReady,
		meErrored,
		reloadMe,
		setMe,
	} from '$lib/auth.svelte';
	import { decideGate } from '$lib/layout-gate';
	import { capturePageview, captureException, identifyUser, resetUser } from '$lib/posthog';
	import OnboardingWizard from '$lib/components/onboarding/OnboardingWizard.svelte';
	import { initReducedMotion, reducedMotion } from '$lib/reduced-motion';

	let { children }: { children: Snippet } = $props();

	// One combined OS + in-app preference owns motion policy for the whole app.
	// JS-driven transitions consume the store; CSS transitions consume the root
	// data attribute installed by the same helper.
	const destroyReducedMotion = initReducedMotion();
	onDestroy(destroyReducedMotion);

	// Single source of truth for which top-level screen renders. A FAILED /me
	// routes to 'reconnect', NEVER the onboarding wizard (layout-gate.ts) — that
	// was "onboarding restarts each redeploy".
	const gate = $derived(
		decideGate({
			authReady: authReady(),
			isAuthenticated: isAuthenticated(),
			meReady: meReady(),
			meError: meErrored(),
			onboarded: me()?.onboarded ?? null,
		}),
	);

	// Auto-retry /me while stuck on the reconnect screen so a redeploy self-heals
	// without the user clicking Retry.
	$effect(() => {
		if (gate !== 'reconnect') return;
		const t = setTimeout(() => void reloadMe(), 5000);
		return () => clearTimeout(t);
	});

	// Admin-only (settings): reset onboarding so the first-run wizard runs again.
	// setMe flips onboarded=false → the layout gate below re-mounts <OnboardingWizard/>.
	async function handleResetOnboarding(): Promise<void> {
		try {
			const next = await resetOnboarding();
			setMe(next);
			toast.success('Onboarding reset — running the welcome wizard.');
		} catch (err) {
			toast.error('Reset failed', { description: err instanceof Error ? err.message : String(err) });
		}
	}

	// Auth0 login gate (auth.md "Login gate"). Run initAuth() once on mount: it
	// completes any redirect callback then loads the auth state into the reactive
	// store. Until that resolves we show nothing chrome-y; then either the
	// centered sign-in screen (unauthenticated) or the full app (authenticated).
	let signingIn = $state(false);
	$effect(() => {
		initAuth()
			.then(() => {
				// PostHog identity follows the verified Auth0 profile. The
				// SSoT for "who is this session" is the JWT `sub` exposed via
				// profile() (the manager uses the same sub as user_id). If
				// authentication didn't land a profile (signed out / dev-
				// bypass off), fall through unidentified — the SDK records
				// against an anonymous id and merges on a later identify.
				const p = profile();
				if (p?.sub) {
					identifyUser(p.sub, {
						email: p.email ?? null,
						name: p.name ?? null,
						onboarded: me()?.onboarded ?? null,
						is_admin: me()?.isAdmin ?? null,
						dev_bypass: isDevBypass(),
					});
				}
			})
			.catch((err) => {
				console.warn('[layout] initAuth failed', err);
				// The login funnel is otherwise uninstrumented — a broken Auth0
				// round-trip (clock skew, reused code, JWKS outage) would vanish
				// into the console. Capture it before resetting identity.
				captureException(err, { source: 'initAuth' });
				// Auth failed — drop any prior PostHog identity so the next
				// user on the same browser doesn't inherit it.
				resetUser();
			});
	});

	// Manual pageview emit. PostHog's built-in history-API listener is off
	// (capture_pageview: false in posthog.ts) so SvelteKit's client-side
	// router transitions count exactly once each. Registered at top level —
	// afterNavigate only attaches when called during component init.
	afterNavigate(() => {
		capturePageview();
	});

	async function handleSignIn(): Promise<void> {
		signingIn = true;
		try {
			await login();
		} catch {
			// loginWithRedirect navigates away on success; an error here means we
			// never left — re-enable the button so the user can retry.
			signingIn = false;
		}
	}

	// The label for the signed-in user in the sidebar footer (email > name > sub).
	const userLabel = $derived.by(() => {
		const p = profile();
		return p ? (p.email ?? p.name ?? p.sub) : '';
	});

	const activeId = $derived(page.params.sessionId);

	// Inline sidebar filter — matches on session name (title) and filters the
	// SessionList in place as you type.
	let sidebarFilter = $state('');

	$effect(() => {
		registerSW();
		// Only pull sessions once signed in AND onboarded — refreshSessions()
		// awaits an Auth0 token (throws before login), and the sidebar isn't
		// visible while the wizard is up so we'd be fetching for nothing. The
		// "Shared with me" list refreshes alongside on the same gate.
		if (isAuthenticated() && me()?.onboarded === true) {
			void refreshSessions();
			void refreshSharedSessions();
		}
	});

	// Live sidebar + "Scheduled" unread dot. A timer-fired scheduled run creates its
	// chat server-side with no browser involved, so the sidebar wouldn't otherwise know.
	// While the tab is visible, poll to surface new run-chats (refreshSessions merges,
	// preserving running/rename state) and refresh the Scheduled badge. Paused when the
	// tab is hidden; runs immediately on becoming visible; cleared on unmount.
	$effect(() => {
		if (!(isAuthenticated() && me()?.onboarded === true)) return;
		const tick = () => {
			if (typeof document !== 'undefined' && document.visibilityState !== 'visible') return;
			void refreshSessions();
			void pollScheduledBadge();
		};
		void pollScheduledBadge(); // prime the badge on mount
		const timer = setInterval(tick, 45_000);
		const onVisible = () => {
			if (document.visibilityState === 'visible') tick();
		};
		document.addEventListener('visibilitychange', onVisible);
		return () => {
			clearInterval(timer);
			document.removeEventListener('visibilitychange', onVisible);
		};
	});

	async function handleNewSession(): Promise<void> {
		await goto('/');
	}

	// SvelteKit polls `_app/version.json` (60s, configured in svelte.config.js)
	// and flips `updated.current` when a new build is detected. Mirror sayano-erp:
	// once a new version is known, force a full-page navigation on the next
	// route change so the user lands on fresh HTML with current chunk hashes —
	// otherwise the SPA keeps using the old code-split bundles and dynamic
	// imports start 404'ing as the deployed assets get garbage-collected.
	beforeNavigate(({ to, cancel }) => {
		if (updated.current && to?.url) {
			cancel();
			window.location.href = to.url.href;
		}
	});

	// Cross-fade route transitions via the View Transitions API. Falls back to
	// instant navigation in browsers without document.startViewTransition (e.g.
	// Firefox at the time of writing). Cross-fade rule lives in the scoped
	// style block below — Foundations agent owns layout.css, so we keep the
	// animation here as a :global() selector to avoid colliding.
	onNavigate((navigation) => {
		if (typeof document === 'undefined') return;
		if ($reducedMotion) return;
		if (!document.startViewTransition) return;
		return new Promise((resolve) => {
			document.startViewTransition(async () => {
				resolve();
				await navigation.complete;
			});
		});
	});

	// Edge-swipe-from-left to open the mobile sidebar drawer. Only wired on
	// <md screens. Dispatches a window CustomEvent so SidebarMobileOpener
	// (which lives inside Sidebar.Provider, where useSidebar() works) can
	// flip the openMobile flag.
	$effect(() => {
		if (typeof window === 'undefined') return;
		const mql = window.matchMedia('(max-width: 767px)');
		let handle: { destroy: () => void } | null = null;

		const wire = () => {
			handle?.destroy();
			handle = null;
			if (!mql.matches) return;
			handle = edgeSwipe(window, {
				edge: 'left',
				edgeWidth: 24,
				threshold: 50,
				onCommit: () => window.dispatchEvent(new CustomEvent('sayano:open-sidebar')),
			});
		};

		wire();
		mql.addEventListener('change', wire);
		return () => {
			mql.removeEventListener('change', wire);
			handle?.destroy();
		};
	});
</script>

<svelte:head>
	<title>Sayano AI</title>
	<link rel="icon" href={favicon} />
</svelte:head>

{#if gate === 'auth-pending' || gate === 'me-pending'}
	<!-- Auth state or the /me round-trip still resolving (the latter incl. a
	     retry across a manager redeploy). A bare centered spinner avoids
	     flashing the gate or the app. -->
	<div class="flex h-svh items-center justify-center bg-background">
		<div
			class="size-6 animate-spin rounded-full border-2 border-border border-t-primary"
			aria-label="Loading"
			role="status"
		></div>
	</div>
{:else if gate === 'login'}
	<!-- Login gate (auth.md "Login gate"): a single clean centered Sign-in
	     screen. The app `children` are never mounted until authenticated, so no
	     REST/WS call ever fires without a token. -->
	<div class="flex h-svh items-center justify-center bg-background px-6">
		<div
			class="flex w-full max-w-sm flex-col items-center gap-6 rounded-2xl border border-border bg-card px-8 py-12 text-center shadow-[var(--shadow-card)]"
		>
			<span class="flex items-center gap-2.5 text-lg font-semibold tracking-[-0.01em]">
				<span class="inline-block size-3 shrink-0 rounded-full bg-primary" aria-hidden="true"
				></span>
				Sayano-AI
			</span>
			<p class="text-[13.5px] text-[color:var(--fg-faint)]">
				Sign in to access your assistant, tools, and skills.
			</p>
			<button
				type="button"
				class="inline-flex min-h-11 w-full items-center justify-center rounded-md bg-primary px-4 py-2.5 text-sm font-medium text-primary-foreground shadow-[var(--shadow-card)] transition-opacity hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 disabled:opacity-60"
				onclick={handleSignIn}
				disabled={signingIn}
			>
				{signingIn ? 'Redirecting…' : 'Sign in'}
			</button>
		</div>
	</div>
{:else if gate === 'reconnect'}
	<!-- /me couldn't be reached (e.g. the manager is mid-redeploy). Show a
	     reconnect state — NEVER the onboarding wizard, which would re-trigger
	     onboarding for already-onboarded users — and auto-retry until it recovers. -->
	<div class="flex h-svh items-center justify-center bg-background px-6">
		<div
			class="flex w-full max-w-sm flex-col items-center gap-5 rounded-2xl border border-border bg-card px-8 py-12 text-center shadow-[var(--shadow-card)]"
		>
			<span class="flex items-center gap-2.5 text-lg font-semibold tracking-[-0.01em]">
				<span class="inline-block size-3 shrink-0 rounded-full bg-primary" aria-hidden="true"></span>
				Sayano-AI
			</span>
			<p class="text-[13.5px] text-[color:var(--fg-faint)]">
				Couldn’t reach the server — it may be redeploying. Retrying…
			</p>
			<button
				type="button"
				class="inline-flex min-h-11 w-full items-center justify-center rounded-md bg-primary px-4 py-2.5 text-sm font-medium text-primary-foreground shadow-[var(--shadow-card)] transition-opacity hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 disabled:opacity-60"
				onclick={() => void reloadMe()}
			>
				Retry now
			</button>
		</div>
	</div>
{:else if gate === 'onboarding'}
	<!-- Mandatory first-run gate (roadmap.md #4). The wizard renders INSTEAD of
	     the app shell — sidebar/composer/marketplace are unreachable until the
	     user finishes. setMe() in the wizard flips state.me.onboarded → true,
	     this branch re-evaluates, and the app shell mounts; no goto() needed. -->
	<OnboardingWizard />
{:else}
<Sidebar.Provider>
	<SidebarMobileOpener />
	<Sidebar.Root>
		<Sidebar.Header class="pt-[env(safe-area-inset-top)]">
			<!-- Wordmark + inline actions, per V1 "Codex light" playground. The
			     compact "+ New" sits on the right so the header stays one row —
			     no full-width New-session button below.
			     pt-[env(safe-area-inset-top)] on the header keeps the wordmark
			     below the iPhone notch when running as an installed PWA. -->
			<div class="flex items-center justify-between gap-2 px-3.5 pt-3.5 pb-2.5">
				<span class="flex items-center gap-2 text-[13px] font-semibold tracking-[-0.01em] text-sidebar-foreground">
					<span
						class="inline-block size-2.5 shrink-0 rounded-full bg-primary"
						aria-hidden="true"
					></span>
					Sayano-AI
				</span>
				<div class="flex items-center gap-1.5">
					<button
						type="button"
						class="inline-flex min-h-11 items-center gap-1 rounded-md border border-sidebar-border bg-background px-3 py-2 text-[13px] font-medium text-[color:var(--fg-body)] shadow-[var(--shadow-card)] transition-all duration-200 ease-[var(--ease-spring)] hover:border-sidebar-ring hover:text-sidebar-foreground motion-safe:hover:-translate-y-px active:translate-y-px focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-sidebar-ring sm:min-h-0 sm:px-2 sm:py-1 sm:text-[11px]"
						aria-label="New session"
						title="New session"
						onclick={handleNewSession}
					>
						<PlusIcon class="size-3" />
						New
					</button>
				</div>
			</div>
		</Sidebar.Header>
		<Sidebar.Content>
			<!-- Inline filter. Matches the V1 playground's full-width search
			     input — filters the SessionList by name in place as you type. -->
			<div class="px-3.5 pb-2">
				<input
					type="search"
					placeholder="Search sessions…"
					bind:value={sidebarFilter}
					class="w-full rounded-md border border-sidebar-border bg-background px-2.5 py-2 text-base text-sidebar-foreground placeholder:text-[color:var(--fg-faint)] outline-none transition-[border-color,box-shadow] duration-200 ease-[var(--ease-spring)] focus:border-sidebar-ring focus:ring-[3px] focus:ring-sidebar-ring/35 sm:py-1 sm:text-[12px]"
					aria-label="Search sessions"
				/>
			</div>
			<Sidebar.Group>
				<Sidebar.GroupContent>
					<SessionList {activeId} filter={sidebarFilter} />
				</Sidebar.GroupContent>
			</Sidebar.Group>
			<!-- Chats others shared with me — kept in its own collapsible group,
			     visually distinct from the user's own time-bucketed chats above. -->
			<Sidebar.Group>
				<Sidebar.GroupContent>
					<SharedWithMeList />
				</Sidebar.GroupContent>
			</Sidebar.Group>
			<!-- Trash (SAY-1463) — soft-deleted chats, restorable or permanently
			     deletable. Collapsed by default; only renders when non-empty. -->
			<Sidebar.Group>
				<Sidebar.GroupContent>
					<TrashList />
				</Sidebar.GroupContent>
			</Sidebar.Group>
		</Sidebar.Content>
		<Sidebar.Footer class="mt-auto gap-1 border-t border-sidebar-border pb-[env(safe-area-inset-bottom)]">
			<!-- Tools-and-skills entry (formerly "Marketplace"). Styled to match the
			     InstallPrompt row below it — a quiet footer link, not a primary
			     action. aria-current highlights it on the route. -->
			{#if me()?.isAdmin}
				<!-- Compact always-visible usage-limit meter (admin-only): mini bars for
				     the Claude/OpenAI subscription windows; links to the full Providers widget. -->
				<SidebarUsage />
				<a
					href="/marketplace/admin"
					aria-current={page.url.pathname.startsWith('/marketplace/admin') ? 'page' : undefined}
					class="flex items-center gap-2 rounded-md px-2.5 py-1.5 text-xs font-medium text-sidebar-foreground transition-colors hover:bg-sidebar-accent hover:text-sidebar-accent-foreground aria-[current=page]:bg-sidebar-accent aria-[current=page]:text-sidebar-accent-foreground"
				>
					<ShieldIcon class="size-3.5" />
					Admin
				</a>
			{/if}
			<!-- Published reports (migration 013) — manage your shareable HTML reports
			     and open the ones colleagues shared with the team. -->
			<a
				href="/reports"
				aria-current={page.url.pathname.startsWith('/reports') ? 'page' : undefined}
				class="flex items-center gap-2 rounded-md px-2.5 py-1.5 text-xs font-medium text-sidebar-foreground transition-colors hover:bg-sidebar-accent hover:text-sidebar-accent-foreground aria-[current=page]:bg-sidebar-accent aria-[current=page]:text-sidebar-accent-foreground"
			>
				<ChartColumnIcon class="size-3.5" />
				Reports
			</a>
			<!-- Scheduled chats (migration 017) — a saved prompt that re-runs itself on a
			     schedule; each run is a real chat. Its own destination next to Reports /
			     Tools and skills (NOT folded into the New-chat button). -->
			<a
				href="/scheduled"
				aria-current={page.url.pathname.startsWith('/scheduled') ? 'page' : undefined}
				class="flex items-center gap-2 rounded-md px-2.5 py-1.5 text-xs font-medium text-sidebar-foreground transition-colors hover:bg-sidebar-accent hover:text-sidebar-accent-foreground aria-[current=page]:bg-sidebar-accent aria-[current=page]:text-sidebar-accent-foreground"
			>
				<ClockIcon class="size-3.5" />
				Scheduled
				{#if scheduledHasUnread()}
					<span
						class="ml-auto inline-block size-1.5 shrink-0 rounded-full bg-primary"
						aria-label="new scheduled run"
						title="A scheduled run finished since you last looked"
					></span>
				{/if}
			</a>
			<!-- JEV Testing Ground (migration 028, jev.md) — try TypeSafe's typed-decision
			     model on company presets. Visible to everyone; the money/usage block
			     inside is admin-only. -->
			<a
				href="/testing-ground"
				aria-current={page.url.pathname.startsWith('/testing-ground') ? 'page' : undefined}
				class="flex items-center gap-2 rounded-md px-2.5 py-1.5 text-xs font-medium text-sidebar-foreground transition-colors hover:bg-sidebar-accent hover:text-sidebar-accent-foreground aria-[current=page]:bg-sidebar-accent aria-[current=page]:text-sidebar-accent-foreground"
			>
				<FlaskConicalIcon class="size-3.5" />
				Testing Ground
			</a>
			<a
				href="/marketplace"
				aria-current={page.url.pathname.startsWith('/marketplace') &&
				!page.url.pathname.startsWith('/marketplace/admin')
					? 'page'
					: undefined}
				class="flex items-center gap-2 rounded-md px-2.5 py-1.5 text-xs font-medium text-sidebar-foreground transition-colors hover:bg-sidebar-accent hover:text-sidebar-accent-foreground aria-[current=page]:bg-sidebar-accent aria-[current=page]:text-sidebar-accent-foreground"
			>
				<BlocksIcon class="size-3.5" />
				Tools and skills
			</a>
			<InstallPrompt />
			<!-- Signed-in identity + logout (auth.md "Login gate"). A quiet footer
			     row: the user's email (truncated) on the left, a sign-out control on
			     the right. The label is display-only — identity travels on the token. -->
			<div class="flex items-center gap-2 px-2.5 py-1.5 text-xs text-sidebar-foreground">
				<span class="min-w-0 flex-1 truncate" title={userLabel}>{userLabel}</span>
					<!-- Light/dark toggle ($lib/theme.svelte). Defaults to dark; choice
					     persists to localStorage; the app.html pre-paint script restores it
					     flash-free on reload. Shows the icon of the mode you'd switch TO. -->
					<button
						type="button"
						class="group inline-flex shrink-0 items-center gap-1 rounded-md px-1.5 py-1 font-medium text-[color:var(--fg-faint)] transition-colors hover:bg-sidebar-accent hover:text-sidebar-accent-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-sidebar-ring"
						onclick={toggleTheme}
						aria-label={isDark() ? 'Switch to light mode' : 'Switch to dark mode'}
						title={isDark() ? 'Switch to light mode' : 'Switch to dark mode'}
					>
						{#if isDark()}
							<SunIcon class="size-3.5 transition-transform duration-300 ease-[var(--ease-spring)] motion-safe:group-hover:rotate-45" />
						{:else}
							<MoonIcon class="size-3.5 transition-transform duration-300 ease-[var(--ease-spring)] motion-safe:group-hover:-rotate-12" />
						{/if}
					</button>
				{#if me()?.isAdmin}
					<button
						type="button"
						class="inline-flex shrink-0 items-center gap-1 rounded-md px-1.5 py-1 font-medium text-[color:var(--fg-faint)] transition-colors hover:bg-sidebar-accent hover:text-sidebar-accent-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-sidebar-ring"
						onclick={handleResetOnboarding}
						aria-label="Reset onboarding"
						title="Reset onboarding — run the welcome wizard again (admin)"
					>
						<RotateCcwIcon class="size-3.5" />
					</button>
				{/if}
				<button
					type="button"
					class="inline-flex shrink-0 items-center gap-1 rounded-md px-1.5 py-1 font-medium text-[color:var(--fg-faint)] transition-colors hover:bg-sidebar-accent hover:text-sidebar-accent-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-sidebar-ring"
					onclick={() => {
						// Clean PostHog identity on explicit sign-out so a
						// subsequent different login on the same browser doesn't
						// merge into the prior person. logout() redirects away.
						resetUser();
						logout();
					}}
					aria-label="Sign out"
					title="Sign out"
				>
					<LogOutIcon class="size-3.5" />
					Sign out
				</button>
			</div>
		</Sidebar.Footer>
	</Sidebar.Root>
	<Sidebar.Inset class="min-w-0">
		<!-- overflow-hidden: scrolling belongs to MessageFeed and the side
		     panels, not <main>. Without this, a long Todo list or a permission
		     backlog in the chat page pushes its `h-svh` column past the viewport
		     and the outer page scrolls — which we never want.
		     min-w-0 on Sidebar.Inset: as a flex-row child of Sidebar.Provider it
		     defaults to min-width:auto, so a wide descendant (long tool path,
		     pre block) would grow it past the mobile viewport and horizontally
		     scroll the page. Sheet-mode sidebar is off-canvas, so this still
		     allows the inset to take 100% width on mobile. -->
		<main class="h-svh overflow-hidden">
			{@render children()}
		</main>
	</Sidebar.Inset>
</Sidebar.Provider>
{/if}

{#if updated.current}
	<UpdateBanner />
{/if}

<Toaster richColors position="bottom-right" closeButton />

<style>
	/* The combined preference is installed on <html>, making every state swap
	   instant even for utility-class transitions and animations. */
	:global([data-reduced-motion] *),
	:global([data-reduced-motion] *::before),
	:global([data-reduced-motion] *::after) {
		animation: none !important;
		transition: none !important;
	}

	/* View Transitions cross-fade for SvelteKit route navigation. Foundations
	   agent owns layout.css, so we keep this rule scoped here with :global()
	   selectors targeting the pseudo-elements the View Transitions API
	   creates on the document root. */
	:global(::view-transition-old(root)),
	:global(::view-transition-new(root)) {
		animation-duration: 200ms;
		animation-timing-function: cubic-bezier(0.32, 0.72, 0, 1);
	}
</style>
